/**
 * Provides the `createSnackbar` utility function for creating a snackbar, and its associated `CreateSnackbarOptions` type.
 * @module createSnackbar
 */
// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
// Constants
const DEFAULT_PARENT_ELEMENT = document.body;
const DEFAULT_POSITION = 'bottom-center';
const DEFAULT_DURATION = -1;
const DEFAULT_IS_FIXED = true;
const DEFAULT_COLOR = '';
/**
 * Creates and appends a `<uo-snackbar>` custom element to the DOM with the specified message and configuration options.
 *
 * **Behavior:**
 * - Takes an `options` object containing the `message` and other optional configurations.
 * - If the required `message` is empty or null/undefined, it logs a warning and returns `undefined`.
 * - Creates a `<uo-snackbar>` element instance.
 * - Assigns properties (`message`, `isFixed`, `position`, `duration`, `color`) to the element based on the provided options or defaults.
 * - Defaults:
 * - `$parentElement`: `document.body`
 * - `isFixed`: `true`
 * - `position`: `'bottom-center'`
 * - `duration`: `-1` (infinite)
 * - `color`: `''` (default appearance)
 * - If `iconID` is provided and not empty, it injects a `<uo-icon>` element into the snackbar's `icon` slot using `insertAdjacentHTML`. An optional `iconAnimation` can also be included.
 * - Appends the configured `<uo-snackbar>` element to the specified `$parentElement`.
 * - If appending fails (e.g., invalid parent element), it logs a warning, attempts to remove the created snackbar element, and returns `undefined`.
 * - Returns the created `<uo-snackbar>` element upon successful creation and appending.
 *
 * @since 5.0 Initial implementation.
 * @param {CreateSnackbarOptions} options - The configuration options for the Snackbar.
 * @param {string} options.message - The message to display in the Snackbar. This is required.
 * @param {HTMLElement} [options.$parentElement=document.body] - The parent HTML element to which the Snackbar will be appended.
 * @param {boolean} [options.isFixed=true] - Whether the Snackbar should be positioned as 'fixed' or 'absolute'.
 * @param {SnackbarPosition} [options.position='bottom-center'] - The position of the Snackbar on the screen (e.g., 'top-right', 'bottom-center').
 * @param {number} [options.duration=-1] - The duration in milliseconds for the Snackbar to remain visible. Use -1 for indefinite duration.
 * @param {string} [options.iconID] - Optional. The ID for a `<uo-icon>` to be displayed within the Snackbar.
 * @param {string} [options.iconAnimation] - Optional. The animation style for the icon (e.g., 'spin', 'pulse'), if an `iconID` is provided.
 * @param {SnackbarColor} [options.color=''] - Optional. The color scheme of the Snackbar (e.g., 'error', or '' for default).
 * @return {Snackbar | undefined} The created `<uo-snackbar>` HTML element, or `undefined` if the message is empty or appending fails.
 *
 * @example Importing the createSnackbar utility
 * ```typescript
 * import { createSnackbar } from '@automator/shared/utils/create-snackbar';
 * ```
 *
 * @example Basic snackbar with default options
 * Creates a snackbar with the message "Task completed!" that appears at the bottom-center, is fixed, and stays indefinitely.
 * ```typescript
 * const $snackbarElement: Snackbar | undefined = createSnackbar( {
 *     message: __( 'Task completed!', 'uncanny-automator' )
 * } );
 *
 * if ( $snackbarElement ) {
 *     // Optionally interact with the element later
 * }
 * ```
 *
 * @example Error snackbar with icon, custom duration, and parent
 * Creates an error-styled snackbar with an icon, makes it disappear after 10 seconds, and appends it inside a specific container element instead of the body.
 * ```typescript
 * createSnackbar( {
 *     message: __( 'Failed to save changes.', 'uncanny-automator' ),
 *     $parentElement: this.$parentElement,
 *     iconID: 'xmark',
 *     color: 'error',
 *     duration: 10000, // 10 seconds
 *     isFixed: false // Position relative to this.$parentElement (if positioned)
 * } );
 * ```
 *
 * @example Snackbar with icon and animation
 * Creates a snackbar with a spinning icon.
 * ```typescript
 * createSnackbar( {
 *     message: __( 'Processing...', 'uncanny-automator' ),
 *     iconID: 'spinner-third',
 *     iconAnimation: 'spin',
 *     duration: -1 // Default value. Keep visible until manually removed or updated
 * } );
 * ```
 *
 * @example Handling empty message input
 * Shows that calling with an empty message results in a warning and returns undefined.
 * ```typescript
 * const result: Snackbar | undefined = createSnackbar( { message: '' } );
 * // console.warn( 'Snackbar message is required and cannot be empty.' ) is logged
 * console.log( result ); // => undefined
 * ```
 */
export function createSnackbar({ message, $parentElement = DEFAULT_PARENT_ELEMENT, isFixed = DEFAULT_IS_FIXED, position = DEFAULT_POSITION, duration = DEFAULT_DURATION, iconID, iconAnimation, color = DEFAULT_COLOR, }) {
    // Validate message presence
    if (isEmpty(message)) {
        console.warn('Snackbar message is required and cannot be empty.');
        return undefined;
    }
    // Create Snackbar element.
    // Type assertion to Snackbar because 'uo-snackbar' is registered and corresponds to the Snackbar class.
    const $snackbar = document.createElement('uo-snackbar');
    // Assign properties directly to the custom element.
    // LitElement handles attribute reflection for the properties that should do so.
    $snackbar.message = message;
    $snackbar.isFixed = isFixed;
    $snackbar.position = position;
    $snackbar.duration = duration;
    $snackbar.color = color;
    // Add icon if specified using insertAdjacentHTML for simplicity with attributes
    if (!isEmpty(iconID)) {
        $snackbar.insertAdjacentHTML('beforeend', `
			<uo-icon
				slot="icon"
				id="${String(iconID)}"
				${iconAnimation ? `animation="${iconAnimation}"` : ''}
			></uo-icon>
		`);
    }
    // Append Snackbar to the parent element and return it
    // Ensure $parentElement is a valid DOM node with appendChild method.
    if ($parentElement && typeof $parentElement.appendChild === 'function') {
        $parentElement.appendChild($snackbar);
        return $snackbar;
    }
    else {
        console.warn('Snackbar could not be appended: $parentElement is invalid or does not support appendChild.', $parentElement);
        // Clean up the created snackbar if it cannot be appended
        $snackbar.remove(); // Remove the orphaned element
        return;
    }
}
;
